import Accordion from "@common/components/Accordion";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import Grid from "@mui/material/Grid2";
import React from "react";
import { Location } from "@services/locations/types";
import _ from "lodash";
import { countryMap } from "@assets/images/flags";
import AccordionSummary from "@common/components/Accordion/AccordionSummary";
import AccordionDetails from "@common/components/Accordion/AccordionDetails";
import { Room } from "@services/rooms/types";
import RoomCard from "../../RoomCard";
import { CircularProgress, LinearProgress, Stack } from "@mui/material";
import { AddParticipantContext } from "../..";

type DirectoryTabProps = {
  excludeExternal: boolean;
  search: string;
  cachedLocations: {
    [locationId: string]: Room[];
  };
  fetchRooms: (locationId: string) => Promise<void>;
  loading: boolean;
};

const DirectoryTab = (props: DirectoryTabProps) => {
  const { search, excludeExternal, cachedLocations, fetchRooms, loading } =
    props;
  const { directory, selectedRooms } = React.useContext(AddParticipantContext);
  const context = React.useContext(RoomsRealtimeContext);

  const getTitle = (location: Location) => {
    const countryKey = location.country?.replace(/ /g, "") ?? "";
    const countryIcon = _.get(countryMap, countryKey);
    return (
      <Grid
        container
        direction="row"
        sx={{ width: '100%', alignItems: "center",
          gap: "0.5rem", }}
      >
        <img style={{ maxHeight: "15px", width: "auto" }} src={countryIcon} />
        {location.name}
      </Grid>
    );
  };

  return (
    <Grid container direction="column" sx={{ width: '100%' }}>
      <>
        {loading ? (
          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <>
            {directory
              .filter(
                (location) =>
                  location.name?.toLowerCase().includes(search.toLowerCase()) ||
                  (cachedLocations[location.id ?? ""] !== undefined &&
                    cachedLocations[location.id ?? ""].some((room) =>
                      room.name?.toLowerCase().includes(search.toLowerCase())
                    ))
              )
              .sort((a, b) => ((a.name ?? "") > (b.name ?? "") ? 1 : -1))
              .map((location) => (
                <Accordion
                  key={location.id}
                  onChange={() => fetchRooms(location.id ?? "")}
                >
                  <AccordionSummary>{getTitle(location)}</AccordionSummary>
                  {cachedLocations[location.id ?? ""] === undefined ? (
                    <LinearProgress />
                  ) : (
                    <AccordionDetails>
                      <Grid
                        container
                        direction="column"
                        sx={{ width: '100%', gap: "1rem", }}
                      >
                        {location.name
                          ?.toLowerCase()
                          .includes(search.toLowerCase()) ? (
                          <>
                            {cachedLocations[location.id ?? ""]
                              .filter(
                                (room) =>
                                  (!excludeExternal ||
                                    (excludeExternal && room.managed)) &&
                                  !selectedRooms.some(
                                    (r) => r.id === room.id
                                  ) &&
                                  !context.detail?.meeting?.participants?.some(
                                    (participant) =>
                                      participant.room?.id === room.id
                                  )
                              )
                              .sort((a, b) =>
                                (a.name ?? "") > (b.name ?? "") ? 1 : -1
                              )
                              .map((room) => (
                                <RoomCard key={room.id} room={room} />
                              ))}
                          </>
                        ) : (
                          <>
                            {cachedLocations[location.id ?? ""]
                              .filter(
                                (room) =>
                                  room.name
                                    ?.toLowerCase()
                                    .includes(search.toLowerCase()) &&
                                  (!excludeExternal ||
                                    (excludeExternal && room.managed)) &&
                                  !selectedRooms.some(
                                    (r) => r.id === room.id
                                  ) &&
                                  !context.detail?.meeting?.participants?.some(
                                    (participant) =>
                                      participant.room?.id === room.id
                                  )
                              )
                              .sort((a, b) =>
                                (a.name ?? "") > (b.name ?? "") ? 1 : -1
                              )
                              .map((room) => (
                                <RoomCard key={room.id} room={room} />
                              ))}
                          </>
                        )}
                      </Grid>
                    </AccordionDetails>
                  )}
                </Accordion>
              ))}
          </>
        )}
      </>
    </Grid>
  );
};

export default DirectoryTab;
